import { MdCode, MdFolder, MdFolderOpen, MdSave, MdPlayArrow, MdStop, MdSettings, MdLightbulb, MdGrading, MdSend, MdHistory } from 'react-icons/md';

const EditorHeader = ({
  showExplorer,
  activeFile,
  onShowExplorer,
  onSave,
  onRun,
  onStop,
  onSettings,
  saving,
  lastSaveTime,
  hasPendingUpdates,
  isExecuting = false,
  // AI Feedback props
  showAIFeedback,
  onToggleAIFeedback,
  aiFeedbackCount = 0,
  aiFeedbackLoading = false,
  onToggleExplorer,
  // Rubric props
  showRubric = false,
  onOpenRubric = null,
  // Submissions history props
  showSubmissions = false,
  onOpenSubmissions = null,
  // Submission props
  assignmentId = null,
  isSubmittingAssignment = false,
  onSubmitAssignment = null,
  dueDateStatus = { canSubmit: true, isLate: false, isLocked: false }
}) => {
  return (
    <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleExplorer}
              className="p-2 hover:bg-gray-50/80 rounded-lg text-gray-600 hover:text-gray-800 transition-colors duration-200"
              title={showExplorer ? 'Close Explorer' : 'Open Explorer'}
            >
              {showExplorer ? (
                <MdFolderOpen size={18} />
              ) : (
                <MdFolder size={18} />
              )}
            </button>
            <div className="flex items-center space-x-3">
              <MdCode className="text-xl text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-800">Code Editor</h2>
            </div>
          </div>
          {activeFile && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 truncate">
              <span>·</span>
              <span className="truncate">{
                typeof activeFile === 'string'
                  ? activeFile
                  : activeFile?.file?.name || activeFile?.name || 'Unknown File'
              }</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3 flex-shrink-0">
          {/* Autosave Status */}
          {saving && (
            <div className="flex items-center space-x-1.5 px-2 py-1 rounded-md bg-gray-50/50 border border-gray-200/60">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-yellow-700">Saving</span>
            </div>
          )}
          {!saving && lastSaveTime && (
            <div className="flex items-center space-x-1.5 px-2 py-1 rounded-md bg-gray-50/50 border border-gray-200/60">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-green-700">
                {new Date(lastSaveTime).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}

          {/* Pending updates indicator */}
          {hasPendingUpdates && (
            <div className="flex items-center space-x-1.5 px-2 py-1 rounded-md bg-gray-50/50 border border-gray-200/60">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-blue-700">Syncing</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Rubric */}
            {showRubric && onOpenRubric && (
              <button
                onClick={onOpenRubric}
                className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors duration-200"
                title="View Rubric"
              >
                <MdGrading size={18} />
              </button>
            )}

            {/* Submission History */}
            {showSubmissions && onOpenSubmissions && (
              <button
                onClick={onOpenSubmissions}
                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                title="View My Submissions"
              >
                <MdHistory size={18} />
              </button>
            )}

            {/* Submit Assignment */}
            {assignmentId && onSubmitAssignment && (
              <button
                onClick={onSubmitAssignment}
                disabled={isSubmittingAssignment || dueDateStatus.isLocked}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  dueDateStatus.isLocked
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : dueDateStatus.isLate
                      ? 'bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-400'
                      : 'bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-400'
                }`}
                title={dueDateStatus.isLocked ? 'Submissions are closed' : ''}
              >
                {isSubmittingAssignment ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                ) : (
                  <MdSend size={14} />
                )}
                <span>
                  {dueDateStatus.isLocked ? 'Closed' : dueDateStatus.isLate ? 'Submit Late' : 'Submit'}
                </span>
              </button>
            )}

            {/* AI Feedback Toggle */}
            <button
              onClick={onToggleAIFeedback}
              className={`relative p-2 rounded-lg transition-colors duration-200 ${
                showAIFeedback
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
              title={showAIFeedback ? 'Hide AI Feedback' : 'Show AI Feedback'}
            >
              <MdLightbulb
                size={18}
                className={aiFeedbackLoading ? 'animate-pulse' : ''}
              />
              {aiFeedbackCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {aiFeedbackCount > 9 ? '9+' : aiFeedbackCount}
                </span>
              )}
            </button>

            <button
              onClick={onSettings}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200"
              title="Editor Settings"
            >
              <MdSettings size={18} />
            </button>

            <button
              onClick={onSave}
              className="flex items-center space-x-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm font-medium"
            >
              <MdSave size={16} />
              <span>Save</span>
            </button>

            {!isExecuting ? (
              <button
                onClick={onRun}
                className="flex items-center space-x-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
              >
                <MdPlayArrow size={16} />
                <span>Run</span>
              </button>
            ) : (
              <button
                onClick={onStop}
                className="flex items-center space-x-1.5 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium"
              >
                <MdStop size={16} />
                <span>Stop</span>
              </button>
            )}
          </div>
        </div>
    </div>
  );
};

export default EditorHeader;